import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';
import { RootModel } from '../models';
import { storeName as locationGroupEntityStore } from '../entities/locationGroup.entity';
import { storeName as projectModelStore } from './project.model';
import { LocationGroupTypes, MapLocationTypes } from '../../types';
import { storeName as mapLocationEntityStore } from '../entities/mapLocation.entity';
import { storeName as templateCategoryEntityStore } from '../entities/templateCategory.entity';
import {
  LanguageTypes,
  LOCATION_LANGUAGE_PATH,
  MODULE_PAGE_LINK,
  SHARE_LINK_KEY,
} from '../../commons/constants';
import { storeName as layoutModelStore } from './layout.model';

export const storeName = 'locationOverviewModel';

type LocationOverviewModelTypes = {
  isMapInitial: boolean;
  groupId: number;
  mapStyle: string;
  subGroupId: number;
  focusItemId: number;
  isFetchedTemplateCategories: boolean;
};

const initialState: LocationOverviewModelTypes = {
  isMapInitial: false,
  groupId: null,
  mapStyle: null,
  subGroupId: null,
  focusItemId: null,
  isFetchedTemplateCategories: false,
};

export const locationOverviewModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setMapInitial: produce((state, payload: boolean) => {
      state.isMapInitial = payload;
    }),
    setGroupData: produce((state, payload: { id: number; key: string }) => {
      if (!payload) {
        state.groupId = initialState.groupId;

        return;
      }

      state.groupId = payload.id;
    }),
    setSubGroupData: produce((state, payload: { id: number; key: string }) => {
      if (!payload) {
        state.subGroupId = initialState.subGroupId;
        return;
      }

      state.subGroupId = payload.id;
    }),
    setConfig: produce(
      (
        state,
        payload: { mapStyle: string; satelliteMode: boolean; numberOfDisplayItem?: number }
      ) => {
        state.mapStyle = payload.mapStyle;
      }
    ),
    setFocusItemId: produce((state, payload: number) => {
      state.focusItemId = payload || initialState.focusItemId;
    }),
    setFetchedTemplateCategories: produce((state, payload: boolean) => {
      state.isFetchedTemplateCategories = payload || initialState.isFetchedTemplateCategories;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async fetchLocationData(_, state) {
      const result: { groups: LocationGroupTypes[]; items: MapLocationTypes[] } = await dispatch[
        locationGroupEntityStore
      ].loadByProjectKey();
      if (!result || !result.groups || !result.groups.length) return;
      dispatch[projectModelStore].setLocationGroupIds(result.groups.map((i) => +i.id));

      if (!result || !result.items || !result.items.length) return;
      dispatch[mapLocationEntityStore].checkTemplateCategory(result.items);
    },
    async fetchTemplateCategory(_, state) {
      if (!state[storeName].isFetchedTemplateCategories) {
        dispatch[storeName].setFetchedTemplateCategories(true);
      } else return;

      await dispatch[templateCategoryEntityStore].loadByProjectKey();
    },
    async setGroupInfo(id: number, state) {
      if (!id || id === state[storeName].groupId) return;
      dispatch[storeName].setGroupData({ id });
    },
    setLocationOverviewInfo(payload, state) {
      const { groupKey = state[storeName].groupId, focusItemKey, itemKey } = payload;

      if (groupKey) {
        this.setGroupInfo(groupKey);
      } else {
        this.setGroupData();
      }

      if (focusItemKey) {
        this.setFocusItemId(focusItemKey);
      } else {
        this.setFocusItemId();
      }

      if (itemKey) {
        this.setItemId(itemKey);
      }
    },
    setViewOnLocalMap(_, state) {
      const language: LanguageTypes = state[layoutModelStore].locale;

      //  check language and replace projectKey and activeKey
      const linkPageLocation = dispatch[projectModelStore]
        .getHomePageLink({ link: MODULE_PAGE_LINK })
        .replace(':projectKey', state[projectModelStore].projectKey)
        .replace(':activeKey', LOCATION_LANGUAGE_PATH[language.locale.toUpperCase()]);

      const { focusItemId, groupId } = state[storeName];
      const arrayParamsLinkLocation: string[] = [];

      if (groupId) {
        arrayParamsLinkLocation.push(`${SHARE_LINK_KEY.LOCATION_GROUP}=${groupId}`);
      }
      if (focusItemId) {
        arrayParamsLinkLocation.push(`${SHARE_LINK_KEY.LOCATION_FOCUS_ITEM}=${focusItemId}`);
      }

      let fullLinkLocation = `${process.env.REACT_APP_DOMAIN}${linkPageLocation}`;
      if (arrayParamsLinkLocation.length) {
        fullLinkLocation += `#${arrayParamsLinkLocation.join('&')}`;
      }
      dispatch[projectModelStore].setIframeContentData({
        visible: true,
        url: fullLinkLocation,
      });
    },
    async fetchLocationGroupById(id: number) {
      const items: MapLocationTypes[] = await dispatch[locationGroupEntityStore].loadById(id);

      if (!items || !items.length) return;
      dispatch[mapLocationEntityStore].checkTemplateCategory(items);
    },
  }),
});

export default locationOverviewModel;
